/**
 * <collection-filter-bar>
 *
 * Drives the horizontal ("top bar") collection filter layout:
 *  - Opens one filter dropdown at a time (closes the others).
 *  - Closes the open dropdown on outside click or Escape.
 *  - Keeps the active dropdown open after the filter form re-renders
 *    its markup via AJAX (the built-in collection-filters-form replaces
 *    the [data-collection-sidebar] innerHTML on every change).
 *
 * This element wraps [data-collection-sidebar] so it persists across
 * those re-renders.
 */
(function () {
  'use strict';

  const SELECTORS = {
    sidebar: '[data-collection-sidebar]',
    summary: '[data-filter-bar-summary]',
    details: '[data-filter-bar-details]',
    key: 'data-filter-bar-key',
  };

  class CollectionFilterBar extends HTMLElement {
    constructor() {
      super();
      this.openKey = null;
      this.onClick = this.onClick.bind(this);
      this.onBodyClick = this.onBodyClick.bind(this);
      this.onKeyup = this.onKeyup.bind(this);
      this.onMutation = this.onMutation.bind(this);
    }

    connectedCallback() {
      this.sidebar = this.querySelector(SELECTORS.sidebar);

      this.addEventListener('click', this.onClick);
      document.addEventListener('click', this.onBodyClick);
      document.addEventListener('keyup', this.onKeyup);

      if (this.sidebar && 'MutationObserver' in window) {
        this.observer = new MutationObserver(this.onMutation);
        this.observer.observe(this.sidebar, { childList: true, subtree: true });
      }
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
      document.removeEventListener('click', this.onBodyClick);
      document.removeEventListener('keyup', this.onKeyup);
      if (this.observer) this.observer.disconnect();
    }

    closeAll() {
      this.querySelectorAll(SELECTORS.details).forEach((details) => {
        details.open = false;
      });
    }

    onClick(event) {
      const summary = event.target.closest(SELECTORS.summary);
      if (!summary || !this.contains(summary)) return;

      // Take over toggling so we can close the other dropdowns.
      event.preventDefault();

      const details = summary.closest(SELECTORS.details);
      if (!details) return;

      const willOpen = !details.open;
      this.closeAll();

      if (willOpen) {
        details.open = true;
        this.openKey = details.getAttribute(SELECTORS.key);
      } else {
        this.openKey = null;
      }
    }

    onBodyClick(event) {
      if (this.contains(event.target)) return;
      this.closeAll();
      this.openKey = null;
    }

    onKeyup(event) {
      if (event.key !== 'Escape' && event.key !== 'Esc') return;
      this.closeAll();
      this.openKey = null;
    }

    onMutation() {
      // After an AJAX re-render, re-open the dropdown the shopper was using.
      if (!this.openKey) return;
      const details = this.querySelector(
        '[' + SELECTORS.key + '="' + this.openKey + '"]'
      );
      if (details && !details.open) {
        details.open = true;
      }
    }
  }

  if (!customElements.get('collection-filter-bar')) {
    customElements.define('collection-filter-bar', CollectionFilterBar);
  }
})();
